/**
 * HTTP Client — Fetch wrapper with JWT auto-refresh.
 *
 * All API calls go through `apiClient` which:
 *  1. Automatically attaches the access token to every request
 *  2. If a 401 is received, attempts to refresh the token
 *  3. Retries the original request with the new token
 *  4. If refresh fails, logs the user out
 */

const API_BASE = import.meta.env.VITE_API_URL || '/api/v1';

interface TokenPair {
  access_token: string;
  refresh_token: string;
}

let accessToken: string | null = null;
let refreshToken: string | null = null;

// Shared promise para coordenar refresh entre múltiplas requests simultâneas
let refreshPromise: Promise<string> | null = null;

// ---- Token management ----

export function setTokens(tokens: TokenPair): void {
  accessToken = tokens.access_token;
  refreshToken = tokens.refresh_token;
  localStorage.setItem('access_token', tokens.access_token);
  localStorage.setItem('refresh_token', tokens.refresh_token);
}

export function clearTokens(): void {
  accessToken = null;
  refreshToken = null;
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
}

export function loadTokens(): void {
  accessToken = localStorage.getItem('access_token');
  refreshToken = localStorage.getItem('refresh_token');
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function hasTokens(): boolean {
  return !!accessToken && !!refreshToken;
}

// ---- Refresh ----

async function refreshAccessToken(): Promise<string> {
  if (!refreshToken) {
    throw new Error('No refresh token');
  }

  const response = await fetch(`${API_BASE}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });

  if (!response.ok) {
    // Só 401/403 significam "refresh token inválido". 429/5xx são falhas
    // temporárias: manter os tokens para tentar de novo depois.
    if (response.status === 401 || response.status === 403) {
      clearTokens();
    }
    throw new Error(`Refresh failed (${response.status})`);
  }

  const data: TokenPair = await response.json();
  setTokens(data);
  return data.access_token;
}

/**
 * Garante que apenas um refresh ocorra por vez.
 * Todas as requests que recebem 401 aguardam a mesma Promise.
 */
function getRefreshedToken(): Promise<string> {
  if (!refreshPromise) {
    refreshPromise = refreshAccessToken().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

// ---- API Client ----

export interface ApiError {
  status: number;
  message: string;
  detail?: unknown;
}

export class ApiRequestError extends Error {
  status: number;
  detail?: unknown;

  constructor(status: number, message: string, detail?: unknown) {
    super(message);
    this.status = status;
    this.detail = detail;
    this.name = 'ApiRequestError';
  }
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  skipAuth?: boolean;
}

async function request<T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const { body, skipAuth = false, ...init } = options;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init.headers as Record<string, string>),
  };

  if (!skipAuth && accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const config: RequestInit = {
    ...init,
    headers,
  };

  if (body !== undefined) {
    config.body = JSON.stringify(body);
  }

  let response = await fetch(`${API_BASE}${endpoint}`, config);

  // Auto-refresh on 401: todas as requests aguardam a mesma Promise de refresh
  if (response.status === 401 && !skipAuth && refreshToken) {
    try {
      const newToken = await getRefreshedToken();
      headers['Authorization'] = `Bearer ${newToken}`;
      response = await fetch(`${API_BASE}${endpoint}`, {
        ...config,
        headers,
      });
    } catch (err) {
      // refreshAccessToken já limpou os tokens se a sessão for inválida de fato.
      // Se ainda há tokens, foi falha temporária (429/5xx/rede): não desloga.
      if (hasTokens()) {
        throw new ApiRequestError(503, 'Serviço temporariamente indisponível. Tente novamente.');
      }
      window.location.href = '/login';
      throw new ApiRequestError(401, 'Session expired');
    }
  }

  if (!response.ok) {
    let detail: unknown;
    try {
      detail = await response.clone().json();
    } catch {
      detail = await response.text();
    }
    const message =
      typeof detail === 'object' && detail !== null && 'detail' in detail
        ? String((detail as Record<string, unknown>).detail)
        : `Request failed with status ${response.status}`;
    throw new ApiRequestError(response.status, message, detail);
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

// ---- Convenience methods ----

export const apiClient = {
  get: <T>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'GET' }),

  post: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'POST', body }),

  patch: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'PATCH', body }),

  put: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'PUT', body }),

  delete: <T>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'DELETE' }),
};
